import { api } from "../api/client.js";
import { shell } from "../ui/layout.js";
import { escapeHtml } from "../ui/dom.js";
import { scoreQuiz, type Quiz, type Question, type Answers } from "../quiz/scoring.js";

// Quiz content bundled from JSON (validated against quiz.schema.json in CI).
const loaded = import.meta.glob("../content/quizzes/*.json", { eager: true });
const quizzes: Quiz[] = Object.values(loaded).map((m) => (m as { default: Quiz }).default);

type Phase = "start" | "question" | "end" | "review";
interface State {
  quiz: Quiz;
  queue: Question[];
  current: number;
  answers: Answers;
  submitted: Record<number, boolean>;
  retakeIds: number[] | null;
  phase: Phase;
  saveStatus: string;
}

let st: State | null = null;
let mount: HTMLElement | null = null;

const flatQ = (q: Quiz): Question[] => q.sections.flatMap((s) => s.questions);
const sectionOf = (q: Quiz, id: number): string =>
  q.sections.find((s) => s.questions.some((x) => x.id === id))?.title ?? "";
const isCorrect = (q: Question, sel: number[]): boolean =>
  q.ans.length === sel.length && q.ans.every((v) => sel.includes(v));
const failedIds = (): number[] =>
  !st ? [] : flatQ(st.quiz).filter((q) => st!.submitted[q.id] && !isCorrect(q, st!.answers[q.id] ?? [])).map((q) => q.id);

export async function quizPage(root: HTMLElement): Promise<void> {
  const content = await shell(root, "quiz");
  st = null;
  content.innerHTML = `<div class="quiz-app" id="qroot"></div>`;
  mount = content.querySelector<HTMLElement>("#qroot");
  renderPicker();
}

// ── Quiz hub (shown only when no quiz is active) ──
function renderPicker(): void {
  if (!mount) return;
  st = null;
  mount.innerHTML = `
    <div class="quiz-titlecard">
      <h1>Quiz Hub</h1>
      <p>Pick a quiz. Choose your answer and Submit — you'll get instant feedback, then it moves on automatically.</p>
    </div>
    <div class="tab-bar">
      ${quizzes
        .map(
          (q) =>
            `<button class="tab-btn ${q.locked ? "locked" : ""}" ${q.locked ? "disabled" : ""} data-quiz="${escapeHtml(q.id)}">${q.locked ? '<span class="lock">🔒</span>' : ""}${escapeHtml(q.title)}</button>`,
        )
        .join("")}
    </div>
    ${quizzes.length ? "" : '<div class="locked-msg"><div class="icon">📋</div><h3>No quizzes available yet</h3></div>'}`;
  mount.querySelectorAll<HTMLButtonElement>("button[data-quiz]").forEach((b) =>
    b.addEventListener("click", () => selectQuiz(b.dataset.quiz ?? "")),
  );
}

function selectQuiz(id: string): void {
  const quiz = quizzes.find((q) => q.id === id);
  if (!quiz || quiz.locked) return;
  st = { quiz, queue: [], current: 0, answers: {}, submitted: {}, retakeIds: null, phase: "start", saveStatus: "" };
  render();
}

function render(): void {
  if (!mount || !st) return;
  if (st.phase === "start") renderStart();
  else if (st.phase === "question") renderQuestion();
  else if (st.phase === "end") renderEnd();
  else renderReview();
}

// Compact top bar during an active quiz (replaces the big hub header → no scrolling).
const bar = (title: string): string =>
  `<div class="quiz-bar"><button class="quiz-back" id="backHub">← Quizzes</button><span class="quiz-bar-title">${escapeHtml(title)}</span></div>`;
function attachBack(): void {
  mount?.querySelector<HTMLButtonElement>("#backHub")?.addEventListener("click", renderPicker);
}

function renderStart(): void {
  if (!mount || !st) return;
  const isRetake = st.retakeIds !== null;
  const count = isRetake ? st.retakeIds!.length : flatQ(st.quiz).length;
  mount.innerHTML =
    bar(st.quiz.title) +
    `<div class="start-screen">
      <h2>${escapeHtml(st.quiz.title)}</h2>
      ${isRetake ? `<div class="retake-banner"><span>⚡</span><span>Retake mode — only the <strong>${count}</strong> question${count > 1 ? "s" : ""} you missed.</span></div>` : ""}
      <div class="start-stats">
        <div class="start-stat"><div class="num">${count}</div><div class="lbl">Questions</div></div>
        <div class="start-stat"><div class="num">${st.quiz.sections.length}</div><div class="lbl">Sections</div></div>
        <div class="start-stat"><div class="num">∞</div><div class="lbl">Retakes</div></div>
      </div>
      <button class="btn-primary" id="startBtn">${isRetake ? "Start Retake" : "Start Quiz"}</button>
    </div>`;
  attachBack();
  mount.querySelector<HTMLButtonElement>("#startBtn")?.addEventListener("click", begin);
}

function begin(): void {
  if (!st) return;
  const all = flatQ(st.quiz);
  st.queue = st.retakeIds !== null ? all.filter((q) => st!.retakeIds!.includes(q.id)) : all;
  if (st.retakeIds === null) {
    st.answers = {};
    st.submitted = {};
  }
  st.current = 0;
  st.phase = "question";
  render();
}

function renderQuestion(): void {
  if (!mount || !st) return;
  const q = st.queue[st.current];
  if (!q) return;
  const total = st.queue.length;
  const locked = !!st.submitted[q.id];
  const pct = Math.round(((st.current + (locked ? 1 : 0)) / total) * 100);
  const multi = q.ans.length > 1;
  const selected = st.answers[q.id] ?? [];

  const opts = q.opts
    .map((opt, i) => {
      const sel = selected.includes(i);
      let cls = "option";
      if (sel) cls += " selected";
      if (locked) {
        cls += " locked";
        if (q.ans.includes(i)) cls += " review-correct";
        else if (sel) cls += " review-wrong";
      }
      return `<div class="${cls}" data-opt="${i}"><div class="${multi ? "option-check" : "option-radio"}">${multi ? "✓" : ""}</div><span>${String.fromCharCode(65 + i)}. ${escapeHtml(opt)}</span></div>`;
    })
    .join("");

  mount.innerHTML =
    bar(st.quiz.title) +
    `<div class="progress-wrap">
      <div class="progress-meta"><span class="progress-label">Question ${st.current + 1} of ${total}</span><span class="progress-section">${escapeHtml(sectionOf(st.quiz, q.id))}</span></div>
      <div class="progress-track"><div class="progress-fill" style="width:${pct}%"></div></div>
    </div>
    <div class="question-card">
      <div class="q-num">Q${st.current + 1}${multi ? ' <span class="q-hint">— select all that apply</span>' : ""}</div>
      <div class="q-text">${escapeHtml(q.q)}</div>
      <div class="options">${opts}</div>
      ${locked && q.exp ? `<div class="review-explanation"><strong>Explanation:</strong> ${escapeHtml(q.exp)}</div>` : ""}
      <div class="action-bar">${
        locked
          ? `<button class="btn-primary" id="advBtn">${st.current === total - 1 ? "See Results →" : "Next →"}</button>`
          : `<button class="btn-primary" id="submitBtn" ${selected.length ? "" : "disabled"}>Submit</button>`
      }</div>
    </div>`;
  attachBack();

  if (locked) {
    mount.querySelector<HTMLButtonElement>("#advBtn")?.addEventListener("click", () => {
      if (!st) return;
      if (st.current === st.queue.length - 1) finishQuiz();
      else {
        st.current++;
        render();
      }
    });
    return;
  }

  mount.querySelectorAll<HTMLElement>(".option").forEach((o) =>
    o.addEventListener("click", () => toggleSelect(q, Number(o.dataset.opt), multi)),
  );
  mount.querySelector<HTMLButtonElement>("#submitBtn")?.addEventListener("click", () => {
    if (!st || !(st.answers[q.id] ?? []).length) return;
    st.submitted[q.id] = true;
    render();
  });
}

function toggleSelect(q: Question, idx: number, multi: boolean): void {
  if (!st || st.submitted[q.id]) return;
  const cur = st.answers[q.id] ?? [];
  st.answers[q.id] = multi ? (cur.includes(idx) ? cur.filter((i) => i !== idx) : [...cur, idx]) : [idx];
  render();
}

function finishQuiz(): void {
  if (!st) return;
  const { score, total } = scoreQuiz(st.quiz, st.answers);
  st.saveStatus = "Saving…";
  st.phase = "end";
  render();
  void submitResult(st.quiz.id, score, total);
}

async function submitResult(quizId: string, score: number, total: number): Promise<void> {
  let status: string;
  try {
    await api.submitQuiz(quizId, score, total);
    status = "✓ Your result has been recorded.";
  } catch {
    status = "⚠ Could not record your result — please try again.";
  }
  if (st) st.saveStatus = status;
  const el = document.getElementById("submitStatus");
  if (el) {
    el.textContent = status;
    el.className = "save-msg " + (status.startsWith("✓") ? "save-ok" : "save-warn");
  }
}

function renderEnd(): void {
  if (!mount || !st) return;
  const res = scoreQuiz(st.quiz, st.answers);
  const pct = res.total ? Math.round((res.score / res.total) * 100) : 0;
  const grade = pct >= 80 ? "Excellent work!" : pct >= 60 ? "Good effort" : "Keep practising";
  const msg =
    pct >= 80
      ? "You have a strong understanding of the material. Well done."
      : pct >= 60
        ? "You are on the right track. Review the questions you missed and try again."
        : "Take some time to revisit the study guides, then try again.";
  const failed = failedIds();
  mount.innerHTML =
    bar(st.quiz.title) +
    `<div class="end-screen">
      <div class="end-hero">
        <div class="end-score-ring"><div class="end-score-pct">${pct}%</div><div class="end-score-sub">${res.score} / ${res.total}</div></div>
        <div class="end-grade">${grade}</div>
        <div class="end-message">${msg}</div>
        <div id="submitStatus" class="save-msg ${st.saveStatus.startsWith("✓") ? "save-ok" : st.saveStatus.startsWith("⚠") ? "save-warn" : ""}">${escapeHtml(st.saveStatus)}</div>
        <div class="end-actions">
          ${failed.length ? `<button class="btn-outline" id="retakeFailed">Retake Failed (${failed.length})</button>` : ""}
          <button class="btn-outline" id="retakeAll">Retake Full Quiz</button>
          <button class="btn-primary" id="reviewBtn">Review Answers</button>
        </div>
      </div>
      <div class="section-breakdown">
        <div class="breakdown-title">Performance by Section</div>
        ${res.sections
          .map((s) => {
            const c = s.pct >= 70 ? "pass" : s.pct >= 50 ? "mid" : "fail";
            return `<div class="breakdown-row"><div class="breakdown-meta"><span class="breakdown-name">${escapeHtml(s.title)}</span><span class="breakdown-pct ${c}">${s.pct}%</span></div><div class="breakdown-bar"><div class="breakdown-fill ${c}" style="width:${s.pct}%"></div></div></div>`;
          })
          .join("")}
      </div>
    </div>`;
  attachBack();
  mount.querySelector<HTMLButtonElement>("#retakeFailed")?.addEventListener("click", () => {
    st!.retakeIds = failedIds();
    st!.phase = "start";
    render();
  });
  mount.querySelector<HTMLButtonElement>("#retakeAll")?.addEventListener("click", () => {
    st!.retakeIds = null;
    st!.answers = {};
    st!.submitted = {};
    st!.phase = "start";
    render();
  });
  mount.querySelector<HTMLButtonElement>("#reviewBtn")?.addEventListener("click", () => {
    st!.phase = "review";
    render();
  });
}

function renderReview(): void {
  if (!mount || !st) return;
  let html =
    bar(st.quiz.title) +
    `<div class="review-header"><span class="review-title">Full Review</span><button class="btn-outline" id="backResults">← Back to Results</button></div>`;
  for (const section of st.quiz.sections) {
    html += `<div class="review-section-divider">${escapeHtml(section.title)}</div>`;
    for (const q of section.questions) {
      const selected = st.answers[q.id] ?? [];
      const answered = !!st.submitted[q.id];
      const correct = answered && isCorrect(q, selected);
      const sc = !answered ? "skipped" : correct ? "correct" : "wrong";
      const sym = !answered ? "—" : correct ? "✓" : "✗";
      const multi = q.ans.length > 1;
      const opts = q.opts
        .map((opt, i) => {
          let cls = "option locked";
          if (q.ans.includes(i)) cls += " review-correct";
          else if (selected.includes(i)) cls += " review-wrong";
          return `<div class="${cls}"><div class="${multi ? "option-check" : "option-radio"}">${multi ? "✓" : ""}</div><span>${String.fromCharCode(65 + i)}. ${escapeHtml(opt)}</span></div>`;
        })
        .join("");
      html += `<div class="review-card was-${correct ? "correct" : "wrong"}">
        <div class="review-q-header"><div class="review-status ${sc}">${sym}</div><div class="review-q-text">${escapeHtml(q.q)}</div></div>
        <div class="options">${opts}</div>
        ${q.exp ? `<div class="review-explanation"><strong>Explanation:</strong> ${escapeHtml(q.exp)}</div>` : ""}
      </div>`;
    }
  }
  mount.innerHTML = html;
  attachBack();
  mount.querySelector<HTMLButtonElement>("#backResults")?.addEventListener("click", () => {
    st!.phase = "end";
    render();
  });
}
